//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Copy, Check } from 'lucide-react';
import { Button } from '../../ui/button';

interface UserResponseProps {
  message: string;
  isHtml?: boolean;
}

const UserResponse: React.FC<UserResponseProps> = ({ message, isHtml }) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimerRef.current = setTimeout(() => setIsHovered(false), 200);
  };

  // Render nothing only if message is explicitly null/undefined. Allow empty string to render structure.
  if (message === null || message === undefined) return null;

  const handleCopy = async () => {
    let textToCopy: string;
    if (isHtml) {
      const safeForCopy = DOMPurify.sanitize(message);
      const doc = new DOMParser().parseFromString(safeForCopy, 'text/html');
      textToCopy = doc.body.textContent ?? message;
    } else {
      textToCopy = message;
    }
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  // Sanitize HTML message if HTML rendering is enabled
  const safeMessage = isHtml ? DOMPurify.sanitize(message) : message;

  return (
    <div
      id="id-user-response-container"
      data-testid="user-response-container"
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        margin: '6px 0 32px 0',
      }}
    >
      <div
        data-testid="user-response-hover-wrapper"
        className="relative"
        style={{ maxWidth: '75%' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          id="id-user-message-box"
          data-testid="user-message-box"
          className="scrollbar-thin overflow-y-auto shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
          style={{
            backgroundColor: '#5C6FCB',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: 400,
            fontFamily: 'Roboto',
            lineHeight: '1.5',
            margin: 0,
            maxWidth: '100%',
            maxHeight: '320px',
          }}
        >
          {isHtml ? (
            <div
              id="id-user-response-html-message"
              data-testid="user-response-html-message"
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {parse(safeMessage)}
            </div>
          ) : (
            <span
              id="id-user-response-plain-message"
              data-testid="user-response-plain-message"
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                display: 'block',
              }}
            >
              {safeMessage}
            </span>
          )}
        </div>
        {isHovered && (
          <div
            className="absolute z-10"
            style={{ bottom: '-24px', right: '0' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Button
              id="id-user-response-copy-button"
              data-testid="user-response-copy-button"
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              title="Copy"
              className="size-6 p-0.5"
              style={{ color: copied ? '#4caf50' : 'rgba(0,0,0,0.35)' }}
            >
              {copied ? (
                <Check data-testid="CheckIcon" className="size-4" />
              ) : (
                <Copy data-testid="ContentCopyIcon" className="size-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserResponse;
